import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import bowser from 'yola-bowser';
import { designSystem } from '@yola/ws-ui';

const {
  Icon,
  Paragraph,
  DropdownContent,
  DropdownTrigger,
  Dropdown,
  ActionList,
  ActionListItem,
  ActionListDivider,
} = designSystem;

// eslint-disable-next-line yola/react/prop-types
const DeletePageTitleComponent = ({ children }) => (
  <Paragraph appearance="danger">{children}</Paragraph>
);

function PageManagerItem({
  item = null,
  captions,
  onSetHomePageClick,
  onPageSettingsClick,
  onDeletePageClick,
  onCopyPageClick,
  onDeveloperConsoleClick,
  onTranslatePageClick,
  onItemClick,
  activeItem = '',
  isHomePage,
  isDeveloperConsoleEnabled,
}) {
  const onItemClickHandler = useCallback(() => {
    onItemClick(item);
  }, [onItemClick, item]);

  const onSetHomePageClickHandler = useCallback(
    (e) => {
      onSetHomePageClick(e, item);
    },
    [onSetHomePageClick, item]
  );

  const onCopyPageClickHandler = useCallback(
    (e) => {
      onCopyPageClick(e, item.id);
    },
    [onCopyPageClick, item.id]
  );

  const onDeveloperConsoleClickHandler = useCallback(
    (e) => {
      onDeveloperConsoleClick(e, item);
    },
    [onDeveloperConsoleClick, item]
  );

  const onPageSettingsClickHandler = useCallback(
    (e) => {
      onPageSettingsClick(e, item);
    },
    [onPageSettingsClick, item]
  );

  const onTranslatePageClickHandler = useCallback(() => {
    onTranslatePageClick(item.id);
  }, [onTranslatePageClick, item.id]);

  const onDeletePageClickHandler = useCallback(
    (e) => {
      onDeletePageClick(e, item);
    },
    [onDeletePageClick, item]
  );

  if (!item) return null;

  const isActivePage = item.id === activeItem;
  const isMobile = Boolean(bowser.mobile || bowser.tablet);
  const isDeveloperConsoleAvailable = isDeveloperConsoleEnabled && isActivePage;

  const className = classNames('ws-page-manager__item', {
    'ws-page-manager__item-active': isActivePage,
  });

  const portalRootNode = document.querySelector('.ws-page-manager-modal');

  return (
    <div className={className} onClick={onItemClickHandler}>
      <Paragraph isClipped>
        <span className="ws-page-manager__item-logo">
          <Icon glyph={isHomePage ? 'home' : 'page'} size={20} />

          {item.password && (
            <span className="ws-page-manager__key-logo">
              <Icon glyph="key" size={12} type="light" strokeWidth={1.5} />
            </span>
          )}
        </span>
        <span translate="no">{item.name || item.title}</span>
      </Paragraph>
      <div className="ws-page-manager__item-controls">
        <Dropdown
          placement="bottom-right"
          onClick={(e) => e.stopPropagation()}
          portalRootNode={portalRootNode}
        >
          <DropdownTrigger>
            <span className="ws-page-manager__item-control-icon">
              <Icon glyph="more-2" size={isMobile ? 16 : 20} type="inherit" />
            </span>
          </DropdownTrigger>
          <DropdownContent>
            <ActionList>
              {!isHomePage && (
                <>
                  <ActionListItem
                    onClick={onSetHomePageClickHandler}
                    title={captions.setHomePage}
                    iconGlyph="home"
                    iconPlacement="left"
                  />
                  <ActionListDivider />
                </>
              )}
              <ActionListItem
                onClick={onCopyPageClickHandler}
                title={captions.copyPage}
                iconGlyph="copy"
                iconPlacement="left"
              />
              <ActionListItem
                onClick={onPageSettingsClickHandler}
                title={captions.pageSettings}
                iconGlyph="settings"
                iconPlacement="left"
              />
              <ActionListItem
                onClick={onTranslatePageClickHandler}
                title={captions.translatePage}
                iconGlyph="translate"
                iconPlacement="left"
              />
              {isDeveloperConsoleAvailable && (
                <>
                  <ActionListDivider />
                  <ActionListItem
                    onClick={onDeveloperConsoleClickHandler}
                    title={captions.developerConsole}
                    iconGlyph="html"
                    iconPlacement="left"
                  />
                </>
              )}
              {!isHomePage && (
                <>
                  <ActionListDivider />
                  <ActionListItem
                    onClick={onDeletePageClickHandler}
                    title={captions.deletePage}
                    iconGlyph="trash"
                    iconType="error"
                    iconPlacement="left"
                    component={DeletePageTitleComponent}
                  />
                </>
              )}
            </ActionList>
          </DropdownContent>
        </Dropdown>
      </div>
    </div>
  );
}

PageManagerItem.propTypes = {
  onDeletePageClick: PropTypes.func.isRequired,
  onPageSettingsClick: PropTypes.func.isRequired,
  onSetHomePageClick: PropTypes.func.isRequired,
  onCopyPageClick: PropTypes.func.isRequired,
  onDeveloperConsoleClick: PropTypes.func.isRequired,
  onTranslatePageClick: PropTypes.func.isRequired,
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    path: PropTypes.string,
    order: PropTypes.string,
    password: PropTypes.string,
  }),
  captions: PropTypes.shape({
    copyPage: PropTypes.string,
    setHomePage: PropTypes.string,
    pageSettings: PropTypes.string,
    deletePage: PropTypes.string,
    setAsHomePage: PropTypes.string,
    developerConsole: PropTypes.string,
    translatePage: PropTypes.string,
  }).isRequired,
  activeItem: PropTypes.string,
  onItemClick: PropTypes.func.isRequired,
  isHomePage: PropTypes.bool.isRequired,
  isDeveloperConsoleEnabled: PropTypes.bool.isRequired,
};

export default PageManagerItem;
